// client-port.js — the ONE seam between this package and a host app's Medplum
// client plumbing (W5 stage 2, EXPO_REPLATFORM_PLAN.md).
//
// The data modules in this package need a small, fixed set of things from the
// host, in two groups:
//
//   A. The Medplum SESSION (owned by the web app's `medplum-client.js`, which is
//      DOM-bound — localStorage session store, location redirects, config fetch —
//      so it stays in the app):
//        getMedplum / deriveCurrentUser / invalidateCached / loadRawConfig
//        activeLoginProfileId / authHeader
//   B. The HOST PLATFORM, for the write tier (W5 stage 8). These are genuinely
//      per-platform and have no place in a framework-free package:
//        getOutbox      — the offline write queue singleton. Its ENGINE is
//                         @healther/outbox; the STORE (IndexedDB / SQLCipher /
//                         expo-sqlite) and the singleton stay in the host
//                         (AGENTS.md §6).
//        isOnline       — the optimistic connectivity hint (navigator.onLine on
//                         web). A lying hint by design: a network throw from a
//                         real call is the authoritative signal.
//        isNetworkError — transport-failure vs real-HTTP-error classifier.
//        apiUrl         — turn an app-relative path ("/api/v1/…") into the URL
//                         THIS build should hit (same origin on web, an absolute
//                         server origin on a device).
//
// The logged-in user's MedplumClient is always the one that flows through here —
// RLS is the only lock (AGENTS.md §2). The host registers its implementations at
// boot and the modules delegate per call. Phase 2's Expo app registers its own —
// the data modules are written once.
//
// Registration is idempotent and lazy: nothing here runs until a data module
// actually needs the host, and an unregistered function FAILS LOUD (a silent
// default would hide a mis-wired host — AGENTS.md §1.3).

let runtimes = [];

/**
 * Register the host's runtime. Each `impl` is an object (or module namespace)
 * carrying some of the functions above; the FIRST registered impl that has a
 * given function wins, so a host can hand over several sources — the web passes
 * its `medplum-client.js` namespace plus one small object of platform functions.
 *
 *   getMedplum(identity?) → Promise<{ medplum }>   — the LOGGED-IN USER's client
 *   deriveCurrentUser(medplum) → user shape
 *   invalidateCached(...resourceTypes) → Promise<void>
 *   loadRawConfig() → the baked app config object
 *   activeLoginProfileId() → string|null           — restored-session profile id
 *   authHeader() → { Authorization? }              — for the app's OWN server legs
 *   getOutbox() → Promise<Outbox>                  — the offline write queue
 *   isOnline() → boolean
 *   isNetworkError(err) → boolean
 *   apiUrl(path) → string
 *
 * Passing a module namespace object (`import * as client`) is the intended form
 * — properties are read per call, so test-time mocks stay live.
 */
export function registerMedplumRuntime(...impls) {
  runtimes = impls;
}

function req(name) {
  for (const impl of runtimes) {
    if (impl && typeof impl[name] === 'function') return impl[name];
  }
  throw new Error(
    `@healther/medplum-data: no Medplum runtime registered (needed "${name}"). ` +
      `The host app must call registerMedplumRuntime() before using data modules — ` +
      `on web that happens in opd-app/src/lib/medplum-runtime.js.`,
  );
}

export async function getMedplum(identity) {
  return req('getMedplum')(identity);
}

export function deriveCurrentUser(medplum) {
  return req('deriveCurrentUser')(medplum);
}

export async function invalidateCached(...types) {
  return req('invalidateCached')(...types);
}

export function loadRawConfig() {
  return req('loadRawConfig')();
}

export function activeLoginProfileId() {
  return req('activeLoginProfileId')();
}

export function authHeader() {
  return req('authHeader')();
}

export async function getOutbox() {
  return req('getOutbox')();
}

export function isOnline() {
  return req('isOnline')();
}

export function isNetworkError(err) {
  return req('isNetworkError')(err);
}

export function apiUrl(path) {
  return req('apiUrl')(path);
}
